import React, { createContext, useContext, useState, ReactNode } from 'react';
import { getLocale, t, LocaleKey, defaultLocale } from '../locales';

interface LocaleContextType {
  currentLocale: LocaleKey;
  setLocale: (locale: LocaleKey) => void;
  t: (path: string) => string;
  locale: ReturnType<typeof getLocale>;
}

const LocaleContext = createContext<LocaleContextType | undefined>(undefined);

interface LocaleProviderProps {
  children: ReactNode;
  initialLocale?: LocaleKey;
}

export const LocaleProvider: React.FC<LocaleProviderProps> = ({ 
  children, 
  initialLocale = defaultLocale 
}) => {
  const [currentLocale, setCurrentLocale] = useState<LocaleKey>(initialLocale);

  const setLocale = (locale: LocaleKey) => {
    setCurrentLocale(locale);
    // Optionally save to localStorage
    localStorage.setItem('museum-tour-locale', locale);
  };

  const value: LocaleContextType = {
    currentLocale,
    setLocale,
    t: (path: string) => t(currentLocale, path),
    locale: getLocale(currentLocale),
  };

  return (
    <LocaleContext.Provider value={value}>
      {children}
    </LocaleContext.Provider>
  );
};

export const useLocale = (): LocaleContextType => {
  const context = useContext(LocaleContext);
  if (context === undefined) {
    throw new Error('useLocale must be used within a LocaleProvider');
  }
  return context;
};

// Initialize locale from localStorage on app start
export const initializeLocale = (): LocaleKey => {
  const saved = localStorage.getItem('museum-tour-locale') as LocaleKey;
  return saved && ['en', 'ro'].includes(saved) ? saved : defaultLocale;
}; 